import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useNavigation } from "@/contexts/NavigationContext";
import { useAppStore } from "@/stores/appStore";
import {
  getProjectOptions,
  getWorkflowOptions,
} from "@/services/api/@tanstack/react-query.gen";
import type { BreadcrumbItem } from "@/types/navigation.types";

export function useBreadcrumbs(): BreadcrumbItem[] {
  const location = useLocation();
  const { meta } = useNavigation();
  const { currentProject } = useAppStore();
  const { pathname } = location;

  // The breadcrumb branches below key every project/workflow crumb off nav
  // context (`meta`) or the store (`currentProject`) — both populated only by
  // navigating INTO a page. On a deep link (e.g. /workflows/:id) or when
  // arriving via a search-result click, neither is set, so the crumbs vanish.
  // Fall back to resolving the names by the id in the URL so every entry path
  // renders correct breadcrumbs.
  const workflowIdInPath = pathname.match(/^\/workflows\/([^/]+)/)?.[1];
  const projectIdInPath = pathname.match(/^\/productions\/([^/]+)/)?.[1];

  // Resolve the workflow (its name + owning project) by id on the canvas when
  // nav context doesn't already carry it.
  const { data: workflowResp } = useQuery({
    ...getWorkflowOptions({ path: { workflowId: workflowIdInPath ?? "" } }),
    enabled: !!workflowIdInPath && !meta.workflowName,
    staleTime: 60_000,
  });
  const resolvedWorkflowName = meta.workflowName || workflowResp?.data?.name;

  // Project id: nav context / store first, else the URL, else the fetched
  // workflow's project (the canvas URL carries no projectId).
  const resolvedProjectId =
    meta.projectId ||
    currentProject?.id ||
    projectIdInPath ||
    workflowResp?.data?.projectId;

  // Resolve the project name by id when we have an id but no name yet.
  const { data: projectResp } = useQuery({
    ...getProjectOptions({ path: { projectId: resolvedProjectId ?? "" } }),
    enabled: !!resolvedProjectId && !(meta.projectName || currentProject?.name),
    staleTime: 60_000,
  });
  const resolvedProjectName =
    meta.projectName ||
    currentProject?.name ||
    projectResp?.data?.name ||
    "Production";

  return useMemo(() => {
    const breadcrumbs: BreadcrumbItem[] = [];

    // Handle Inspire page
    if (pathname === "/inspire" || pathname === "/") {
      breadcrumbs.push({
        label: "Inspire",
        isCurrentPage: true,
      });
      return breadcrumbs;
    }

    // Handle CoPilot page
    if (pathname === "/copilot") {
      breadcrumbs.push({
        label: "Co-Pilot",
        isCurrentPage: true,
      });
      return breadcrumbs;
    }

    // Handle Bin page
    if (pathname === "/bin") {
      breadcrumbs.push({
        label: "Bin",
        isCurrentPage: true,
      });
      return breadcrumbs;
    }

    // Organisation pages: /organisations/:organisationId/*
    if (pathname.match(/^\/organisations\/[^/]+/)) {
      breadcrumbs.push({
        label: "Organisations",
        isCurrentPage: false,
      });
      breadcrumbs.push({
        label: meta.organisationName || "Organisation",
        isCurrentPage: true,
      });
      return breadcrumbs;
    }

    // Handle Productions page (root without project selected)
    if (pathname === "/productions" && !meta.projectId && !currentProject) {
      breadcrumbs.push({
        label: "Productions",
        isCurrentPage: true,
      });
      return breadcrumbs;
    }

    // For project-related routes, start with Productions as root
    breadcrumbs.push({
      label: "Productions",
      href: "/productions",
      isCurrentPage:
        pathname === "/productions" && !meta.projectId && !currentProject,
    });

    // Early return for root projects (no project selected)
    if (pathname === "/productions" && !meta.projectId && !currentProject) {
      return breadcrumbs;
    }

    // Workflow editor: /workflows/:workflowId
    if (pathname.match(/^\/workflows\/[^/]+/)) {
      // Add project breadcrumb if available
      const projectId = resolvedProjectId;
      const projectName = resolvedProjectName;

      if (projectId) {
        breadcrumbs.push({
          label: projectName,
          href: `/productions/${projectId}/scenes`,
          isCurrentPage: false,
        });
        // Add Workflows intermediate step
        breadcrumbs.push({
          label: "Workflows",
          href: `/productions/${projectId}/workflows`,
          isCurrentPage: false,
        });
      }

      // Add workflow breadcrumb
      breadcrumbs.push({
        label: resolvedWorkflowName || "Workflow",
        isCurrentPage: true,
      });
      return breadcrumbs;
    }

    // Timeline editor: /video-editor/:timelineId
    if (pathname.match(/^\/video-editor\/[^/]+/)) {
      // Add project breadcrumb if available
      const projectId = resolvedProjectId;
      const projectName = resolvedProjectName;

      if (projectId) {
        breadcrumbs.push({
          label: projectName,
          href: `/productions/${projectId}/scenes`,
          isCurrentPage: false,
        });
      }

      // Add timeline breadcrumb
      breadcrumbs.push({
        label: meta.timelineName || "Timeline",
        isCurrentPage: true,
      });
      return breadcrumbs;
    }

    // Project scenes tab: /productions/:projectId/scenes
    if (pathname.match(/^\/productions\/[^/]+\/scenes$/)) {
      const projectId = resolvedProjectId;
      const projectName = resolvedProjectName;

      if (projectId) {
        breadcrumbs.push({
          label: projectName,
          href: `/productions/${projectId}/scenes`,
          isCurrentPage: false,
        });
        breadcrumbs.push({
          label: "Scenes",
          isCurrentPage: true,
        });
      }
      return breadcrumbs;
    }

    // Project workflows tab: /productions/:projectId/workflows
    if (pathname.match(/^\/productions\/[^/]+\/workflows$/)) {
      const projectId = resolvedProjectId;
      const projectName = resolvedProjectName;

      if (projectId) {
        breadcrumbs.push({
          label: projectName,
          href: `/productions/${projectId}/scenes`,
          isCurrentPage: false,
        });
        breadcrumbs.push({
          label: "Workflows",
          isCurrentPage: true,
        });
      }
      return breadcrumbs;
    }

    // Project assets tab: /productions/:projectId/assets
    if (pathname.match(/^\/productions\/[^/]+\/assets$/)) {
      const projectId = resolvedProjectId;
      const projectName = resolvedProjectName;

      if (projectId) {
        breadcrumbs.push({
          label: projectName,
          href: `/productions/${projectId}/scenes`,
          isCurrentPage: false,
        });
        breadcrumbs.push({
          label: "Assets",
          isCurrentPage: true,
        });
      }
      return breadcrumbs;
    }

    // Project cast tab: /productions/:projectId/cast
    // Also covers the detail panel, which the ?cast= param opens in place
    if (pathname.match(/^\/productions\/[^/]+\/cast$/)) {
      const projectId = resolvedProjectId;
      const projectName = resolvedProjectName;

      if (projectId) {
        breadcrumbs.push({
          label: projectName,
          href: `/productions/${projectId}/scenes`,
          isCurrentPage: false,
        });

        // Keyed off nav context, not the ?cast= param: CastContainer sets
        // castName when a cast resolves and clears it in the same
        // effect's cleanup, so this follows the panel opening and closing.
        // Reading location.search instead would also need adding to the useMemo
        // deps below, which pathname alone does not cover.
        if (meta.castName) {
          // Show Cast > Cast Name when detail panel is open
          breadcrumbs.push({
            label: "Cast",
            href: `/productions/${projectId}/cast`,
            isCurrentPage: false,
          });
          breadcrumbs.push({
            label: meta.castName,
            isCurrentPage: true,
          });
        } else {
          breadcrumbs.push({
            label: "Cast",
            isCurrentPage: true,
          });
        }
      }
      return breadcrumbs;
    }

    // Project library tab: /productions/:projectId/library
    if (pathname.match(/^\/productions\/[^/]+\/library$/)) {
      const projectId = resolvedProjectId;
      const projectName = resolvedProjectName;

      if (projectId) {
        breadcrumbs.push({
          label: projectName,
          href: `/productions/${projectId}/scenes`,
          isCurrentPage: false,
        });
        breadcrumbs.push({
          label: "Library",
          isCurrentPage: true,
        });
      }
      return breadcrumbs;
    }

    // Project settings: /productions/:projectId/settings
    if (pathname.match(/^\/productions\/[^/]+\/settings$/)) {
      const projectId = resolvedProjectId;
      const projectName = resolvedProjectName;

      if (projectId) {
        breadcrumbs.push({
          label: projectName,
          href: `/productions/${projectId}/scenes`,
          isCurrentPage: false,
        });
        breadcrumbs.push({
          label: "Settings",
          isCurrentPage: true,
        });
      }
      return breadcrumbs;
    }

    // Cast detail (project-scoped): /productions/:projectId/cast/:castId
    if (pathname.match(/^\/productions\/[^/]+\/cast\/[^/]+$/)) {
      const projectId = resolvedProjectId;
      const projectName = resolvedProjectName;

      if (projectId) {
        breadcrumbs.push({
          label: projectName,
          href: `/productions/${projectId}/scenes`,
          isCurrentPage: false,
        });
        breadcrumbs.push({
          label: "Cast",
          href: `/productions/${projectId}/cast`,
          isCurrentPage: false,
        });
      }
      breadcrumbs.push({
        label: meta.castName || "Cast",
        isCurrentPage: true,
      });
      return breadcrumbs;
    }

    // Cast detail (legacy): /cast/:castId
    if (pathname.match(/^\/cast\/[^/]+$/)) {
      const projectId = resolvedProjectId;
      const projectName = resolvedProjectName;

      if (projectId) {
        breadcrumbs.push({
          label: projectName,
          href: `/productions/${projectId}/scenes`,
          isCurrentPage: false,
        });
        breadcrumbs.push({
          label: "Cast",
          href: `/productions/${projectId}/cast`,
          isCurrentPage: false,
        });
      }
      breadcrumbs.push({
        label: meta.castName || "Cast",
        isCurrentPage: true,
      });
      return breadcrumbs;
    }

    // Project page: /productions/:projectId or dashboard with project selected
    const projectId = resolvedProjectId;
    const projectName = resolvedProjectName;

    if (projectId) {
      breadcrumbs.push({
        label: projectName,
        href: `/productions/${projectId}`,
        isCurrentPage: true,
      });
      return breadcrumbs;
    }

    // Settings pages: /settings/:section
    if (pathname.match(/^\/settings(\/|$)/)) {
      // Reset — settings is not under Projects
      breadcrumbs.length = 0;
      breadcrumbs.push({
        label: "Settings",
        href: "/settings/email",
        isCurrentPage: false,
      });
      const section = pathname.split("/")[2];
      const sectionLabels: Record<string, string> = {
        email: "Email",
        teams: "Teams",
      };
      breadcrumbs.push({
        label: sectionLabels[section] ?? section ?? "Email",
        isCurrentPage: true,
      });
      return breadcrumbs;
    }

    // Custom Services page (under organisations)
    if (pathname.includes("/services")) {
      breadcrumbs.length = 0;
      breadcrumbs.push({
        label: "Custom Services",
        isCurrentPage: true,
      });
      return breadcrumbs;
    }

    // Templates page
    if (pathname === "/templates") {
      breadcrumbs.length = 0;
      breadcrumbs.push({
        label: "Templates",
        isCurrentPage: true,
      });
      return breadcrumbs;
    }

    // 404 or unmatched route
    breadcrumbs.push({
      label: "404 Not Found",
      isCurrentPage: true,
    });

    return breadcrumbs;
  }, [
    pathname,
    meta,
    currentProject,
    resolvedProjectId,
    resolvedProjectName,
    resolvedWorkflowName,
  ]);
}
